<template>
  <div class="content-list">
    <div class="content-header">
      <h2>内容管理</h2>
      <el-button type="primary" @click="showAddDialog">新增内容</el-button>
    </div>

    <el-table :data="contentList" style="width: 100%">
      <el-table-column prop="title" label="标题" width="180" />
      <el-table-column prop="type" label="类型" width="100">
        <template #default="scope">
          {{ scope.row.type === 'news' ? '新闻' : '攻略' }}
        </template>
      </el-table-column>
      <el-table-column prop="status" label="状态" width="100">
        <template #default="scope">
          <el-tag :type="scope.row.status === 'published' ? 'success' : 'info'">
            {{ scope.row.status === 'published' ? '已发布' : '草稿' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="图片" width="120">
        <template #default="scope">
          <el-image 
            v-if="scope.row.image" 
            :src="scope.row.image" 
            :preview-src-list="[scope.row.image]"
            fit="cover"
            style="width: 100px; height: 60px;"
          />
          <span v-else>无图片</span>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="180" />
      <el-table-column prop="updateTime" label="更新时间" width="180" />
      <el-table-column label="操作" width="200">
        <template #default="scope">
          <el-button-group>
            <el-button 
              size="small" 
              @click="handleEdit(scope.row)"
            >编辑</el-button>
            <el-button 
              size="small" 
              type="primary" 
              @click="handlePublish(scope.row)"
              v-if="scope.row.status === 'draft'"
            >发布</el-button>
            <el-button 
              size="small" 
              type="danger" 
              @click="handleDelete(scope.row)"
            >删除</el-button>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogTitle"
      v-model="dialogVisible"
      width="50%"
    >
      <el-form :model="form" label-width="80px">
        <el-form-item label="标题">
          <el-input v-model="form.title" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="form.type">
            <el-option label="新闻" value="news" />
            <el-option label="攻略" value="guide" />
          </el-select>
        </el-form-item>
        <el-form-item label="图片">
          <el-upload
            class="upload-demo"
            action="#"
            :auto-upload="false"
            :show-file-list="false"
            :on-change="handleImageChange"
          >
            <el-button type="primary">选择图片</el-button>
            <div v-if="form.image" class="image-preview">
              <el-image 
                :src="form.image" 
                fit="cover"
                style="width: 200px; height: 120px; margin-top: 10px;"
              />
              <el-button 
                type="danger" 
                size="small" 
                @click.stop="removeImage"
                style="margin-top: 10px;"
              >删除图片</el-button>
            </div>
          </el-upload>
        </el-form-item>
        <el-form-item label="内容">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="10"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSave">保存</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'ContentList',
  setup() {
    const contentList = ref([]);
    const dialogVisible = ref(false);
    const dialogTitle = ref('新增内容');
    const form = reactive({
      id: null,
      title: '',
      type: 'news',
      content: '',
      status: 'draft',
      image: ''
    });

    const showAddDialog = () => {
      dialogTitle.value = '新增内容';
      form.id = null;
      form.title = '';
      form.type = 'news';
      form.content = '';
      form.status = 'draft';
      form.image = '';
      dialogVisible.value = true;
    };

    const handleEdit = (row) => {
      dialogTitle.value = '编辑内容';
      Object.assign(form, row);
      dialogVisible.value = true;
    };

    const handleImageChange = (file) => {
      // 创建图片URL
      const reader = new FileReader();
      reader.onload = (e) => {
        form.image = e.target.result;
      };
      reader.readAsDataURL(file.raw);
    };

    const removeImage = () => {
      form.image = '';
    };

    const handleSave = () => {
      if (!form.title || !form.content) {
        ElMessage.warning('请填写完整信息');
        return;
      }

      let contentList = JSON.parse(localStorage.getItem('contentList') || '[]');

      if (form.id) {
        // 更新
        const index = contentList.findIndex(item => item.id === form.id);
        if (index !== -1) {
          contentList[index] = {
            ...form,
            updateTime: new Date().toLocaleString()
          };
        }
      } else {
        // 新增
        contentList.push({
          ...form,
          id: Date.now(),
          createTime: new Date().toLocaleString(),
          updateTime: new Date().toLocaleString()
        });
      }

      // 保存到本地存储
      localStorage.setItem('contentList', JSON.stringify(contentList));
      // 更新显示
      contentList.value = contentList;

      dialogVisible.value = false;
      ElMessage.success('保存成功');
    };

    const handlePublish = (row) => {
      ElMessageBox.confirm(
        '确定要发布该内容吗？',
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        let contentList = JSON.parse(localStorage.getItem('contentList') || '[]');
        const index = contentList.findIndex(item => item.id === row.id);
        if (index !== -1) {
          contentList[index].status = 'published';
          contentList[index].updateTime = new Date().toLocaleString();
          // 保存到本地存储
          localStorage.setItem('contentList', JSON.stringify(contentList));
          // 更新显示
          contentList.value = contentList;
          ElMessage.success('发布成功');
        }
      });
    };

    const handleDelete = (row) => {
      ElMessageBox.confirm(
        '确定要删除该内容吗？',
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        let contentList = JSON.parse(localStorage.getItem('contentList') || '[]');
        const index = contentList.findIndex(item => item.id === row.id);
        if (index !== -1) {
          contentList.splice(index, 1);
          // 保存到本地存储
          localStorage.setItem('contentList', JSON.stringify(contentList));
          // 更新显示
          contentList.value = contentList;
          ElMessage.success('删除成功');
        }
      });
    };

    // 初始化时从本地存储加载数据
    onMounted(() => {
      const savedContentList = JSON.parse(localStorage.getItem('contentList') || '[]');
      contentList.value = savedContentList;
    });

    return {
      contentList,
      dialogVisible,
      dialogTitle,
      form,
      showAddDialog,
      handleEdit,
      handleSave,
      handlePublish,
      handleDelete,
      handleImageChange,
      removeImage
    };
  }
};
</script>

<style scoped>
.content-list {
  padding: 20px;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.image-preview {
  margin-top: 10px;
  text-align: center;
}

.upload-demo {
  text-align: center;
}
</style> 